// ============================================================================
// SETUP SERVICE — the Setup Checklist (setup/navigation design section 5)
// ============================================================================

import { downloadFile, get } from '../client/axiosClient';
import { buildEndpoint, buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type { ApiResponse } from '../types/common.types';

export type SetupStatus = 'DONE' | 'MISSING' | 'WARNING' | 'NOT_NEEDED' | 'INFO';

export interface SetupChecklistItem {
  /** The check's code in the design (O1, P3, T2…). */
  code: string;
  section: string;
  title: string;
  /** M mandatory, R recommended, I information. */
  level: 'M' | 'R' | 'I';
  status: SetupStatus;
  detail?: string | null;
  /** The web module id of the screen that fixes it. */
  goModuleId?: string | null;
  /** The part of that screen to open for the block's record (e.g. the outlet window's "tax"). */
  goSection?: string | null;
}

export interface SetupChecklistBlock {
  scope: 'TENANT' | 'PROPERTY' | 'OUTLET';
  id?: string | null;
  name: string;
  propertyId?: string | null;
  missing: number;
  warnings: number;
  items: SetupChecklistItem[];
}

export interface SetupChecklist {
  checkedAt: string;
  missing: number;
  warnings: number;
  blocks: SetupChecklistBlock[];
}

export interface ConfigReportRow {
  cells: (string | null)[];
  /** Indexes of cells that are a property's own value (bold). */
  marked: number[];
}

export interface ConfigReportTable {
  title: string;
  columns: string[];
  rows: ConfigReportRow[];
  note?: string | null;
}

export interface ConfigReportSection {
  key: string;
  title: string;
  tables: ConfigReportTable[];
}

export interface ConfigReport {
  generatedAt: string;
  generatedBy?: string | null;
  changedSince: string;
  sections: ConfigReportSection[];
}

export const setupService = {
  /** Worked out live on the server each call; nothing is stored. */
  async getChecklist(): Promise<SetupChecklist> {
    const res = await get<ApiResponse<SetupChecklist>>(buildEndpoint('/setup/checklist'));
    return res.data.data;
  },

  /** The Configuration Report (design section 6); changes listed since the date (yyyy-mm-dd). */
  async getConfigReport(changedSince: string): Promise<ConfigReport> {
    const res = await get<ApiResponse<ConfigReport>>(buildEndpointWithQuery('/setup/config-report', { changedSince }));
    return res.data.data;
  },

  /** The same report as a file made on the server: Excel (a sheet per section) or PDF (with sign-off lines). */
  downloadConfigReport(kind: 'excel' | 'pdf', changedSince: string): Promise<void> {
    const today = new Date().toISOString().slice(0, 10);
    return downloadFile(
      buildEndpointWithQuery(`/setup/config-report/${kind}`, { changedSince }),
      `Configuration-Report-${today}.${kind === 'excel' ? 'xlsx' : 'pdf'}`,
    );
  },
};

export default setupService;
